import { createFileRoute, notFound } from "@tanstack/react-router";
import { z } from "zod";
import { getPublicCmsPage, type PublicCmsPage } from "@/lib/cms-public.functions";
import { getCmsPagePreview } from "@/lib/cms-preview.functions";
import { CmsBlockRenderer } from "@/components/cms/CmsBlockRenderer";
import { sanitizeHtml } from "@/lib/cms-sanitize";
import { SITE_ORIGIN } from "@/lib/seo";

/**
 * Public CMS page route.
 *
 * URL: /p/<slug> (slugs may contain slashes via splat, e.g. /p/services/diagnostics).
 * Renders any published row from `cms_pages`, plus its ordered `cms_page_blocks`.
 *
 * Draft preview: append `?preview=<token>` to render the current draft
 * even if the page is unpublished. Previews are always `noindex` and
 * surface a banner so editors don't confuse them with the live site.
 */

const searchSchema = z.object({
  preview: z.string().min(16).max(128).optional(),
});

export const Route = createFileRoute("/p/$")({
  validateSearch: (s) => searchSchema.parse(s),
  loaderDeps: ({ search }) => ({ preview: search.preview }),
  loader: async ({ params, deps }) => {
    const slug = params._splat ?? "";
    if (!slug) throw notFound();
    const page = deps.preview
      ? await getCmsPagePreview({ data: { token: deps.preview, slug } })
      : await getPublicCmsPage({ data: { slug } });
    if (!page) throw notFound();
    return { page, isPreview: Boolean(deps.preview) };
  },
  head: ({ loaderData }) => {
    const data = loaderData as { page: PublicCmsPage; isPreview: boolean } | undefined;
    if (!data) return { meta: [{ title: "Not found" }] };
    const { page, isPreview } = data;
    const url = `${SITE_ORIGIN}/p/${page.slug}`;
    const canonical = page.canonical_url || url;
    const title = (isPreview ? "[Preview] " : "") + (page.meta_title || page.title);
    const description = page.meta_description || "";
    const meta: Array<Record<string, string>> = [
      { title },
      { property: "og:title", content: title },
      { property: "og:url", content: url },
      { property: "og:type", content: "article" },
    ];
    if (description) {
      meta.push({ name: "description", content: description });
      meta.push({ property: "og:description", content: description });
    }
    if (page.og_image_url) {
      meta.push({ property: "og:image", content: page.og_image_url });
      meta.push({ name: "twitter:image", content: page.og_image_url });
    }
    if (page.noindex || isPreview) meta.push({ name: "robots", content: "noindex,nofollow" });
    return { meta, links: [{ rel: "canonical", href: canonical }] };
  },
  errorComponent: ({ error }) => (
    <main className="container mx-auto px-4 py-16">
      <h1 className="text-2xl font-semibold">Something went wrong</h1>
      <p className="text-muted-foreground mt-2">{error.message}</p>
    </main>
  ),
  notFoundComponent: () => (
    <main className="container mx-auto px-4 py-16">
      <h1 className="text-2xl font-semibold">Page not found</h1>
      <p className="text-muted-foreground mt-2">The page you were looking for has moved, was unpublished, or the preview link expired.</p>
      <a href="/" className="inline-block mt-4 text-primary underline">Back to home</a>
    </main>
  ),
  component: CmsPublicPage,
});

function CmsPublicPage() {
  const { page, isPreview } = Route.useLoaderData();
  const intro = typeof page.body?.html === "string" ? (page.body.html as string) : "";

  return (
    <main className="container mx-auto px-4 py-10 max-w-4xl">
      {isPreview && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm">
          <strong>Draft preview</strong> — this page is not live. Search engines will not index this URL.
        </div>
      )}
      <article className="space-y-8">
        <header className="space-y-3">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{page.title}</h1>
          {page.meta_description && (
            <p className="text-lg text-muted-foreground">{page.meta_description}</p>
          )}
        </header>
        {intro && (
          <div
            className="prose prose-slate dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(intro) }}
          />
        )}
        <CmsBlockRenderer blocks={page.blocks} />
      </article>
    </main>
  );
}
